/**
 * SimplePDFViewer Hook for Admin Proposal Preview
 *
 * A minimal PDF viewer using PDF.js for rendering PDF documents.
 * Based on Akash's working PDFViewer implementation from PR #7876.
 * Uses pushEvent to sync page state with LiveView component.
 */
export const SimplePDFViewer = {
  mounted() {
    this.pdfDocument = null
    this.currentPage = 1
    this.totalPages = 0
    this.scale = 1.0

    // Wait for PDF.js to load from CDN
    this.waitForPdfJs()
      .then(() => {
        this.initializePDFViewer()
      })
      .catch((error) => {
        console.error("Failed to load PDF.js:", error)
        this.showError("Failed to load PDF viewer")
      })

    // Listen for page navigation events from LiveView
    this.handleEvent("navigate_page", (data) => {
      if (data.page === "next") {
        this.nextPage()
      } else if (data.page === "prev") {
        this.previousPage()
      } else if (typeof data.page === "number") {
        this.goToPage(data.page)
      }
    })

    // Add keyboard navigation support
    this.handleKeydown = (e) => {
      // Only handle arrow keys when the modal is open
      if (!this.el || !this.el.offsetParent) return

      if (e.key === "ArrowLeft") {
        e.preventDefault()
        this.previousPage()
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        this.nextPage()
      }
    }

    document.addEventListener("keydown", this.handleKeydown)
  },

  destroyed() {
    if (this.pdfDocument) {
      this.pdfDocument.destroy()
    }
    // Clean up keyboard event listener
    if (this.handleKeydown) {
      document.removeEventListener("keydown", this.handleKeydown)
    }
  },

  /**
   * Wait for PDF.js library to be available
   */
  async waitForPdfJs() {
    return new Promise((resolve, reject) => {
      if (window.pdfjsLib) {
        resolve()
        return
      }

      let attempts = 0
      const maxAttempts = 50 // 5 seconds timeout

      const checkPdfJs = () => {
        if (window.pdfjsLib) {
          resolve()
        } else if (attempts >= maxAttempts) {
          reject(new Error("PDF.js failed to load"))
        } else {
          attempts++
          setTimeout(checkPdfJs, 100)
        }
      }

      checkPdfJs()
    })
  },

  /**
   * Initialize the PDF viewer
   */
  async initializePDFViewer() {
    const canvas = this.el.querySelector("#pdf-canvas")
    const loadingIndicator = this.el.querySelector("#pdf-loading")

    if (!canvas) {
      console.error("PDF canvas not found")
      return
    }

    this.canvas = canvas
    this.context = canvas.getContext("2d")
    this.loadingIndicator = loadingIndicator

    // Get PDF path from component data attribute
    const pdfPath = this.el.dataset.pdfPath

    if (!pdfPath) {
      this.showError("No PDF path provided")
      return
    }

    try {
      this.showLoading(true)
      await this.loadPDF(pdfPath)
      this.showLoading(false)
      this.showStatus("PDF loaded successfully")
    } catch (error) {
      console.error("Error loading PDF:", error)
      this.showLoading(false)
      this.showError(`Failed to load PDF: ${error.message}`)
    }
  },

  /**
   * Load and render PDF document
   */
  async loadPDF(pdfPath) {
    const loadingTask = window.pdfjsLib.getDocument({
      url: pdfPath,
      cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
      cMapPacked: true,
    })

    this.pdfDocument = await loadingTask.promise
    this.totalPages = this.pdfDocument.numPages
    console.log(`PDF loaded: ${this.totalPages} pages`)

    // Push initial page info to LiveView immediately
    const target = this.el.getAttribute("phx-target")
    console.log("Pushing page_changed event to target:", target, "with:", {
      current: 1,
      total: this.totalPages,
    })
    if (target) {
      this.pushEventTo(target, "page_changed", {
        current: 1,
        total: this.totalPages,
      })
    } else {
      this.pushEvent("page_changed", {
        current: 1,
        total: this.totalPages,
      })
    }

    // Render first page
    await this.renderPage(1)
  },

  /**
   * Render a specific page
   */
  async renderPage(pageNumber) {
    if (!this.pdfDocument) return

    const page = await this.pdfDocument.getPage(pageNumber)

    // Get container dimensions to determine appropriate scale
    const container = this.canvas.parentElement
    const containerWidth = container.clientWidth - 32 // Account for padding

    // Calculate scale to fit width
    const initialViewport = page.getViewport({ scale: 1.0 })
    const scale = containerWidth / initialViewport.width
    this.scale = scale

    // Get final viewport with calculated scale
    const viewport = page.getViewport({ scale: scale })

    // Set canvas dimensions
    this.canvas.width = viewport.width
    this.canvas.height = viewport.height

    // Set CSS size to fit container
    this.canvas.style.width = `${viewport.width}px`
    this.canvas.style.height = `${viewport.height}px`
    this.canvas.style.maxWidth = "100%"

    // Render page
    const renderContext = {
      canvasContext: this.context,
      viewport: viewport,
    }

    await page.render(renderContext).promise
    console.log(`Rendered page ${pageNumber}`)

    this.currentPage = pageNumber
    this.totalPages = this.pdfDocument.numPages

    // Update status
    this.showStatus(`Page ${this.currentPage} of ${this.totalPages}`)

    // Notify LiveView of page change
    this.notifyPageChange()

    // Scroll PDF container to top when changing pages
    const pdfContainer = this.el
    if (pdfContainer) {
      pdfContainer.scrollTop = 0
    }
  },

  /**
   * Navigate to next page
   */
  async nextPage() {
    if (this.currentPage < this.totalPages) {
      await this.renderPage(this.currentPage + 1)
    }
  },

  /**
   * Navigate to previous page
   */
  async previousPage() {
    if (this.currentPage > 1) {
      await this.renderPage(this.currentPage - 1)
    }
  },

  /**
   * Go to specific page
   */
  async goToPage(pageNumber) {
    if (pageNumber >= 1 && pageNumber <= this.totalPages) {
      await this.renderPage(pageNumber)
    }
  },

  /**
   * Notify LiveView of page change
   */
  notifyPageChange() {
    const target = this.el.getAttribute("phx-target")
    const eventData = {
      current: this.currentPage,
      total: this.totalPages,
    }

    if (target) {
      this.pushEventTo(target, "page_changed", eventData)
    } else {
      this.pushEvent("page_changed", eventData)
    }
  },

  /**
   * Show status message (following Akash's pattern)
   */
  showStatus(message) {
    console.log("PDF Viewer:", message)
    // Could show in UI status area if needed
  },

  /**
   * Show/hide loading indicator
   */
  showLoading(show) {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = show ? "flex" : "none"
    }
  },

  /**
   * Show error message (following Akash's pattern)
   */
  showError(message) {
    console.error("PDF Viewer Error:", message)

    // Show error in PDF container
    const errorDiv = document.createElement("div")
    errorDiv.className = "pdf-error"
    errorDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.3);
      padding: 10px /* root-scale: legacy-px */;
      border-radius: 3.75px /* root-scale: legacy-px */;
      text-align: center;
      color: rgba(239, 68, 68, 0.9);
      z-index: 10;
    `
    errorDiv.textContent = message

    this.el.appendChild(errorDiv)
  },
}
